import React from 'react';
import styled, { keyframes } from 'styled-components';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { TransitionGroup } from 'react-transition-group';

import NextAction from '../Widget/NextAction';
import Overlay from '../Widget/Overlay';
import OverlayPartner from '../Widget/OverlayPartner';
import OverlayPublisher from '../Widget/OverlayPublisher';
import WidgetHeader from '../Widget/WidgetHeader';

import { Container, Body } from '../DefaultStyles/Details';
import { Footer } from '../DefaultStyles/Shared';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ParentOrg } from '../../components/Volunteer/Shared';
import {
    PBVMFooter,
    ChangeLocationLink,
    volunteerMatchLogoURL
} from '../../components/Volunteer/Shared';
import { Title } from '../DefaultStyles/Shared';
import Carousel from '../../components/Shared/Carousel';
import CssTransition from '../../components/Transition/CssTransition';
import DefaultVolunteerImage from '../../components/Volunteer/DefaultImage';
import LocationChangePopup from '../../components/Volunteer/LocationChangePopup';
import MultiActionsCardAction from '../../components/MultiActionsCard/MultiActionsCardAction';
import ShareDrawer from '../../components/ShareDrawer';
import VolunteerSubscribeForm from '../../components/Volunteer/VolunteerSubscribeForm';

import {
    normalizeOpportunityName,
    replaceLocale,
    truncateOpportunityDescription,
    truncateOpportunityTitle,
    truncateTitle
} from '../../utils/volunteer/helpers';

import { cacheFormData } from '../../utils/formCaching/actions';
import {
    onBackWithinCard,
    onGoToNextActionInsideCard,
    onSubscribeComplete,
    setOverlayPartnerVisible,
    getApiRequestData
} from '../Widget/actions';

import {
    fetchVolunteerOpportunities,
    onSelectVolunteerOpportunity,
    onGoToStep,
    onShowLocationChangePopup,
    onDetailsContainerClick,
    useBackAnimations
} from './actions';

import { ActionTypes, CardTypes } from '../../utils/enums';
import { widthEm, isPortrait } from '../../utils/size';
import { Placeholders } from '../../utils/fillDynamicText';
import {
    getNextAction,
    buildConditionalActionsAnswerHistory
} from '../../utils/nextActionUtil';
import { isLongText } from '../../components/Shared/commonFunctions';

const ShrinkCardWrapper = (from, to) => keyframes`
    from {
        height: ${from}px;
    }
    to {
        height: ${to}px;
    }
`;

const VolunteerBody = styled(Body)`
    flex: 1;
`;

const VolunteerCardWrapper = styled.div`
    background-color: ${props =>
        props.backgroundColor ? props.backgroundColor : `#FFFFFF`};
    display: flex;
    flex: 1;
    padding: 5px 20px 15px 20px;
    height: ${() => (isPortrait() ? 355 : 220)}px;
    ${props =>
        props.sequenceFromDetailsCard
            ? `animation: ${ShrinkCardWrapper(
                  isPortrait() ? 355 : 220,
                  isPortrait() ? 310 : 175
              )} 300ms forwards;`
            : ``};
    position: relative;
    margin: ${props => (props.numActions === 2 && !isPortrait() ? `0 6px` : ``)};
    border-right: 1px solid #f1f1ee;
    box-shadow: 0 2px 4px 0 #e4e4e4;
    border-radius: 4px;
    border: solid 1px #dadad9;
    padding: ${props =>
        props.emailCard ? `15px 10px 10px` : isPortrait() ? `20px 15px 15px` : `15px`};
    ${props =>
        props.emailCard && !isPortrait() && props.numActions > 1
            ? `width: 49% !important;`
            : ''};

    &:focus {
        outline: none;
    }

    &:last-child {
        border-right: ${() => (widthEm() > 30 ? `0px` : ``)};
    }

    svg {
        height: 50px;
        width: 50px;
    }

    &:focus {
        outline: none;
    }

    &:last-child {
        border-right: ${() => (widthEm() > 30 ? `0px` : ``)};
    }

    svg {
        height: 50px;
        width: 50px;
    }

    button {
        width: calc(100% - 20px);
        margin: 0 auto;
        height: 45px;
        position: absolute;
        left: 10px;
        bottom: 10px;
        word-break: break-word;
        span {
            font-size: ${() => (isPortrait() ? 24 : 18)}px;
        }

        &:hover {
            background-color: #ff245e;
            border: solid 1px #ff245e;
            color: #fff;
        }

        &:hover span {
            color: #fff;
        }

        ${props =>
            (widthEm() < 30 || props.isMultiDonation) &&
            `
                bottom: 12px;
                left: 10px;
                padding: 0;
                width: calc(100% - 20px);
            `};
    }
`;

const CardTitle = styled(Title)`
    ${() =>
        !isPortrait() &&
        `
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        `};
    margin: 10px 0;
    word-break: break-all;
    hyphens: auto;
`;

const CardHeader = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
`;

const TopRow = styled.div`
    height: 50px;
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 5px;
`;
const DetailsRow = styled.div`
    display: flex;
    flex-direction: column;
`;

const OpportunityTitle = styled.h3`
    flex: 1;
    font-size: 15px;
    margin: 0;
    margin-left: 5px;
    word-break: break-letter;
    hyphens: auto;
`;

const ParentOrgName = styled(ParentOrg)`
    font-size: 11px;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

const HeaderDetail = styled.h4`
    width: 95%;
    font-size: 10px;
    font-weight: 300;
    text-transform: uppercase;
    margin: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: #000000;
`;

const Description = styled.p`
    font-family: Lato;
    line-height: 1.33;
    color: #000;
    word-break: break-word;
    font-size: ${() => (isPortrait() ? 15 : 14)}px;
    overflow: hidden;
    margin: 5px 0;
    word-break: break-letter;
    hyphens: auto;

    span {
        font-weight: 800;
        color: #ff245e;
        cursor: pointer;
    }
`;

const SpinnerContainer = styled.div`
    width: 100%;
    display: flex;
    flex: 1;
    margin: 0 auto;
    justify-content: center;
    align-content: center;
    position: relative;
`;

const VolunteerCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    width: 100%;

    &.hide {
        display: none;
    }
`;

const PartnerImage = styled.img`
    width: auto !important;
    max-height: ${() => (isPortrait() ? 45 : 43)}px;
    max-width: 90px;
`;

const SubscribeFormWrapper = styled.div`
    height: 100%;
`;

class VolunteerDetails extends React.Component {
    constructor() {
        super();
        this.state = {
            nextActionVisibile: false,
            emailSignedUp: false,
            hover: true
        };
    }

    componentDidMount = () => {
        if (this.props.postalCode && !this.props.volunteerOpportunities) {
            this.loadVolunteerOpportunities();
        }
        if (this.props.card.type === CardTypes.actionChoice) {
            setTimeout(() => {
                this.props.useBackAnimations();
            }, 1000);
        }
    };

    loadVolunteerOpportunities = () => {
        this.props.fetchVolunteerOpportunities(
            this.props.postalCode,
            this.props.countryCode,
            this.props.activeActionId,
            this.props.actionIdentifier,
            false, // preload screen
            false, // popup
            false // cache
        );
    };

    buildAddress = (s1 = '', s2 = '', city, state = '', zip = '') =>
        `${s1} ${s2} ${city ? city + ',' : ''} ${state} ${zip}`;

    render() {
        const carouselSettings = {
            arrows: this.props.actions.length > 2,
            centerMode: false,
            dots: false,
            draggabe: true,
            focusOnSelect: false,
            infinite: false,
            slidesToScroll: 1,
            slidesToShow: isPortrait() ? 1 : 1.5,
            speed: 500,
            swipeToSlide: true,
            preventFinalSlideAdvance: !isPortrait()
        };

        const sequenceFromDetailsCard =
            this.state.emailSignedUp &&
            this.props.actions.length === 1 &&
            this.props.hasNextAction;

        return (
            <Container
                onClick={() =>
                    this.props.onDetailsContainerClick(
                        this.props.actionIdentifier,
                        this.props.actionInitiated
                    )
                }
            >
                <Overlay />
                <OverlayPublisher />
                <OverlayPartner
                    logoIsSmall={false}
                    transitionType="right-to-left"
                    logo={volunteerMatchLogoURL}
                    name={'About Volunteer Match'}
                    description={
                        "We believe everyone should have the chance to make a difference. That's why we make it easy for good people and good causes to connect. We've connected millions of people with a great place to volunteer and helped tens of thousands of organizations better leverage volunteers to create real impact."
                    }
                />
                <VolunteerBody>
                    <WidgetHeader />
                    <CardTitle
                        scaleFactor={isPortrait() ? 0.666 : 0.8}
                        isLongText={isLongText(this.props.headerText, 60)}
                        isExtraLongText={isLongText(this.props.headerText, 90)}
                        onClick={e => {
                            e.preventDefault();
                            this.props.setOverlayPartnerVisible(true, e, false);
                        }}
                        dangerouslySetInnerHTML={{
                            __html: this.props.headerText
                        }}
                    />
                    {this.props.isVolunteerOpportunitiiesLoading ? (
                        <SpinnerContainer>
                            <LoadingSpinner
                                position={'center'}
                                width={80}
                                height={80}
                                stroke={6}
                                color={'#ff245e'}
                            />
                        </SpinnerContainer>
                    ) : (
                        <Carousel
                            carouselSettings={carouselSettings}
                            className={isPortrait() && 'carousel-mobile'}
                            hover={this.state.hover}
                            isVolunteer={true}
                            numActions={this.props.actions.length}
                            onMouseEnter={() => this.setState({ hover: true })}
                            onMouseLeave={() => this.setState({ hover: false })}
                            showSlider={isPortrait() || this.props.actions.length > 1}
                        >
                            {this.props.actions.map((a, i) => {
                                if (a.id === 'signup') {
                                    return (
                                        <VolunteerCardWrapper
                                            sequenceFromDetailsCard={
                                                sequenceFromDetailsCard
                                            }
                                            emailCard={true}
                                            numActions={this.props.actions.length}
                                            isMultiDonation={false}
                                            key={a.id}
                                        >
                                            <SubscribeFormWrapper
                                                numActions={this.props.actions.length}
                                            >
                                                <VolunteerSubscribeForm
                                                    onShowLocationChangePopup={
                                                        this.props
                                                            .onShowLocationChangePopup
                                                    }
                                                    actionIdentifier={
                                                        this.props.actionIdentifier
                                                    }
                                                    numActions={this.props.actions.length}
                                                    onSubmit={values => {
                                                        this.props.cacheFormData(values);
                                                        this.props.onSubscribeComplete(
                                                            values,
                                                            this.props.activeAction.type,
                                                            null,
                                                            null,
                                                            false,
                                                            () => {
                                                                this.setState({
                                                                    emailSignedUp: true
                                                                });
                                                                setTimeout(
                                                                    () =>
                                                                        this.setState({
                                                                            nextActionVisibile: true
                                                                        }),
                                                                    500
                                                                );
                                                            }
                                                        );
                                                    }}
                                                    volunteerForm={true}
                                                    isEmailSubmitting={
                                                        this.props.isEmailSubmitting
                                                    }
                                                    isEmailSubmitted={
                                                        this.state.emailSignedUp
                                                    }
                                                />
                                            </SubscribeFormWrapper>
                                        </VolunteerCardWrapper>
                                    );
                                } else {
                                    return (
                                        <VolunteerCardWrapper
                                            numActions={this.props.actions.length}
                                            isMultiDonation={false}
                                            key={a.id}
                                        >
                                            <VolunteerCard>
                                                <CardHeader>
                                                    <TopRow>
                                                        {a.imageUrl ? (
                                                            <PartnerImage
                                                                src={decodeURIComponent(
                                                                    a.imageUrl
                                                                )}
                                                            />
                                                        ) : (
                                                            <DefaultVolunteerImage
                                                                issueId={
                                                                    this.props
                                                                        .associatedOpportunityIssueId
                                                                }
                                                            />
                                                        )}
                                                        <OpportunityTitle>
                                                            {truncateOpportunityTitle(
                                                                normalizeOpportunityName(
                                                                    a.title
                                                                ),
                                                                isPortrait()
                                                            )}
                                                        </OpportunityTitle>
                                                    </TopRow>
                                                    <DetailsRow>
                                                        <ParentOrgName>
                                                            {a.parentOrg.name}
                                                        </ParentOrgName>
                                                        <HeaderDetail>
                                                            {a.virtual
                                                                ? 'VIRTUAL'
                                                                : this.buildAddress(
                                                                      a.address.street1,
                                                                      a.address.street2,
                                                                      a.address.city,
                                                                      a.address.state,
                                                                      a.address.zipCode
                                                                  )}
                                                        </HeaderDetail>
                                                    </DetailsRow>
                                                </CardHeader>
                                                <Description
                                                    dangerouslySetInnerHTML={{
                                                        __html: truncateOpportunityDescription(
                                                            a.description,
                                                            this.props.actions.length,
                                                            isPortrait()
                                                        )
                                                    }}
                                                    onClick={e =>
                                                        this.props.setOverlayPartnerVisible(
                                                            true,
                                                            e,
                                                            false
                                                        )
                                                    }
                                                />
                                                <MultiActionsCardAction
                                                    action={a}
                                                    isMultiDonation={false}
                                                    tweetContent={a.text || ''}
                                                    key={a.identifier}
                                                    label={'SELECT'}
                                                    showTwitterPopUp={false}
                                                    localRepFound={false}
                                                    partnerName={a.partnerName}
                                                    onClick={() =>
                                                        this.props.onSelectVolunteerOpportunity(
                                                            i,
                                                            this.props.actions
                                                        )
                                                    }
                                                />
                                            </VolunteerCard>
                                        </VolunteerCardWrapper>
                                    );
                                }
                            })}
                        </Carousel>
                    )}
                    <TransitionGroup>
                        {this.props.postalCodeFormPopupIsVisible && (
                            <CssTransition
                                key="alert"
                                in={this.props.postalCodeFormPopupIsVisible}
                            >
                                <LocationChangePopup
                                    onBack={() => {
                                        this.props.onShowLocationChangePopup(
                                            false,
                                            this.props.actionIdentifier,
                                            this.props.actionInitiated
                                        );
                                    }}
                                />
                            </CssTransition>
                        )}
                    </TransitionGroup>
                    {sequenceFromDetailsCard && this.state.nextActionVisibile && (
                        <NextAction />
                    )}
                    <Footer>
                        <ShareDrawer
                            goBack={() =>
                                this.props.card.type === CardTypes.actionChoice
                                    ? this.props.onBackWithinCard(
                                          this.props.actionIdentifier,
                                          this.props.card,
                                          true
                                      )
                                    : null
                            }
                            fullwidth={true}
                            // rightAlign={this.props.card.type !== CardTypes.actionChoice}
                            hasBackButton={
                                this.props.card.type === CardTypes.actionChoice
                            }
                            backgroundColor={sequenceFromDetailsCard ? null : '#ffffff'}
                        >
                            <ChangeLocationLink
                                isActionChoice={
                                    this.props.card.type === CardTypes.actionChoice
                                }
                                onClick={() =>
                                    this.props.onShowLocationChangePopup(
                                        true,
                                        this.props.actionIdentifier,
                                        this.props.actionInitiated
                                    )
                                }
                            >
                                {'Change Location'}
                            </ChangeLocationLink>
                        </ShareDrawer>
                        <PBVMFooter
                            sequenceFromDetailsCard={sequenceFromDetailsCard}
                            onClick={e =>
                                this.props.setOverlayPartnerVisible(true, e, true)
                            }
                        >
                            {'Powered by VolunteerMatch'}
                        </PBVMFooter>
                    </Footer>
                </VolunteerBody>
            </Container>
        );
    }
}

VolunteerDetails.propTypes = {
    actionIdentifier: PropTypes.string.isRequired,
    actionInitiated: PropTypes.bool.isRequired,
    actions: PropTypes.array.isRequired,
    activeAction: PropTypes.object.isRequired,
    activeActionId: PropTypes.number.isRequired,
    associatedOpportunityIssueId: PropTypes.number,
    card: PropTypes.object.isRequired,
    cacheFormData: PropTypes.func.isRequired,
    countryCode: PropTypes.string,
    fetchVolunteerOpportunities: PropTypes.func.isRequired,
    hasNextAction: PropTypes.bool.isRequired,
    headerText: PropTypes.string.isRequired,
    isEmailSubmitting: PropTypes.bool,
    isVolunteerOpportunitiiesLoading: PropTypes.bool,
    onBackWithinCard: PropTypes.func.isRequired,
    onDetailsContainerClick: PropTypes.func.isRequired,
    onSelectVolunteerOpportunity: PropTypes.func.isRequired,
    onShowLocationChangePopup: PropTypes.func.isRequired,
    onSubscribeComplete: PropTypes.func.isRequired,
    postalCode: PropTypes.string,
    postalCodeFormPopupIsVisible: PropTypes.bool.isRequired,
    setOverlayPartnerVisible: PropTypes.func.isRequired,
    useBackAnimations: PropTypes.func.isRequired,
    volunteerOpportunities: PropTypes.array
};

export default connect(
    state => {
        return {
            actions: (state.volunteer.volunteerOpportunities || [])
                .map(a => ({ ...a }))
                .concat({ id: 'signup' }),
            actionIdentifier: state.widget.activeAction.identifier,
            actionInitiated: state.volunteer.actionInitiated,
            activeAction: state.widget.activeAction,
            activeActionId: state.widget.activeAction.id,
            associatedOpportunityIssueId: state.widget.activeAction.issue.id,
            card: state.widget.cards[state.widget.activeAction.order - 1],
            countryCode: state.widget.endUserLocation
                ? state.widget.endUserLocation.countryCode
                : 'US',
            endUserLocation: state.widget.endUserLocation,
            hasNextAction: !!getNextAction(
                state.widget.actions,
                state.widget.activeAction.identifier,
                buildConditionalActionsAnswerHistory(state),
                state.widget.cards
            ),
            headerText: truncateTitle(
                (
                    state.widget.cards[state.widget.activeAction.order - 1].heading ||
                    'Volunteer in your community today!'
                ).replace(
                    new RegExp(Placeholders.LocalePlaceholder, 'g'),
                    replaceLocale(state)
                )
            ),
            isEmailSubmitting: state.widget.isEmailSubmitting,
            isMobileResolution: state.widget.isMobileResolution,
            isMultiActionsCard:
                state.widget.cards[state.widget.activeAction.order - 1].type ===
                CardTypes.multipleActions,
            isVolunteerOpportunitiiesLoading:
                state.volunteer.isVolunteerOpportunitiiesLoading,
            postalCode: state.widget.endUserLocation
                ? state.widget.endUserLocation.postalCode
                : undefined,
            postalCodeFormPopupIsVisible: state.volunteer.postalCodeFormPopupIsVisible,
            volunteerOpportunities: state.volunteer.volunteerOpportunities
        };
    },
    {
        cacheFormData,
        fetchVolunteerOpportunities,
        getApiRequestData,
        onBackWithinCard,
        onDetailsContainerClick,
        onGoToNextActionInsideCard,
        onGoToStep,
        onSelectVolunteerOpportunity,
        onShowLocationChangePopup,
        onSubscribeComplete,
        setOverlayPartnerVisible,
        useBackAnimations
    },
    null,
    {
        areStatesEqual: (next, prev) => {
            if (next.widget.activeAction.type !== ActionTypes.Volunteer) return true;

            return next === prev;
        }
    }
)(VolunteerDetails);
